import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CursorPagination, useCursorPager } from '@nucleus-commerce/admin-kit/data-table';
import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { useOrder } from '@/features/orders';

import { useDiscountUsages } from '../hooks/use-discount-usages';

/** Usages carry the order id only; the order name is read from the (cached) order. */
function OrderLink({ storeId, orderId }: { storeId: string; orderId: string }) {
  const { data } = useOrder(storeId, orderId);
  return (
    <Link
      to="/s/$storeId/orders/$orderId"
      params={{ storeId, orderId }}
      className="font-mono text-[13px] font-medium hover:underline"
    >
      {data?.name ?? orderId}
    </Link>
  );
}

/** Amounts are in each order's currency, so a store selling in several markets shows a mix. */
export function DiscountUsagesCard({
  storeId,
  discountId,
}: {
  storeId: string;
  discountId: string;
}) {
  const { t } = useTranslation('discounts');
  const format = useFormat();
  const [cursor, setCursor] = useState<string | undefined>();
  const pager = useCursorPager({ cursor, onNavigate: setCursor, resetKey: discountId });
  const query = useDiscountUsages(storeId, discountId, cursor);
  const usages = query.data?.items ?? [];

  return (
    <FormSection title={t('usages.title')} description={t('usages.description')}>
      {query.isPending ? (
        <Skeleton className="h-24" />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : usages.length === 0 && !cursor ? (
        <p className="text-muted-foreground text-sm">{t('usages.empty')}</p>
      ) : (
        <>
          <Table aria-label={t('usages.title')}>
            <TableHeader>
              <TableRow>
                <TableHead>{t('usages.order')}</TableHead>
                <TableHead>{t('usages.code')}</TableHead>
                <TableHead>{t('usages.customer')}</TableHead>
                <TableHead className="text-right">{t('usages.amount')}</TableHead>
                <TableHead>{t('usages.when')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usages.map((usage) => (
                <TableRow key={usage.id}>
                  <TableCell>
                    <OrderLink storeId={storeId} orderId={usage.orderId} />
                  </TableCell>
                  <TableCell>
                    {usage.code ? (
                      <code className="text-[13px]">{usage.code}</code>
                    ) : (
                      <span className="text-muted-foreground">{t('kind.automatic')}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {usage.customerId ? (
                      <Link
                        to="/s/$storeId/customers/$customerId"
                        params={{ storeId, customerId: usage.customerId }}
                        className="hover:underline"
                      >
                        {usage.email ?? t('usages.viewCustomer')}
                      </Link>
                    ) : (
                      <span className="flex flex-col">
                        {usage.email ? <span>{usage.email}</span> : null}
                        <span className="text-muted-foreground text-xs">{t('usages.guest')}</span>
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {format.money(usage.amount)}{' '}
                    <span className="text-muted-foreground text-xs">{usage.amount.currency}</span>
                  </TableCell>
                  <TableCell className="text-muted-foreground" title={format.utc(usage.createdAt)}>
                    {format.relative(usage.createdAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <CursorPagination
            pager={pager}
            nextCursor={query.data.nextCursor}
            isFetching={query.isFetching}
            count={usages.length}
          />
        </>
      )}
    </FormSection>
  );
}
